import {
  useLayoutEffect,
  useState,
  RefObject,
  type CSSProperties
} from 'react';

import {
  getFlippedPosition,
  calculateBasePosition,
  applyNudge,
  calculateArrowOffset
} from './useSmartTooltipPosition.utils';

/**
 * The position of the tooltip relative to the anchor element.
 */
export type TooltipPosition = 'up' | 'down' | 'left' | 'right';

export interface IUseSmartTooltipPositionProps {
  /** Whether the tooltip is currently active. */
  isActive: boolean;

  /** The preferred position of the tooltip relative to the anchor. */
  preferredPosition: TooltipPosition;

  /** A reference to the tooltip element. */
  tooltipRef: RefObject<HTMLDivElement | null>;

  /** A reference to the anchor element that the tooltip is positioned relative to. */
  anchorRef: RefObject<HTMLElement | null>;

  /**
   * The minimum distance in pixels from the viewport edges to keep the tooltip visible.
   * @default - 15
   */
  threshold?: number;
}

/**
 * The result of the `useSmartTooltipPosition` hook, containing the final position,
 * styles for the tooltip, and styles for the arrow.
 */
export interface IUseSmartTooltipPositionResult {
  /** The final position of the tooltip after applying flip logic and nudging. */
  finalPosition: TooltipPosition;

  /** The CSS styles to apply to the tooltip for positioning. */
  nudgeStyle: CSSProperties;

  /** The coordinates for the tooltip's position. */
  coordinates: CSSProperties;

  /** The styles for the arrow of the tooltip, if applicable. */
  triangleOffset: CSSProperties['left'] | CSSProperties['top'];
}

/**
 * A custom hook to manage the position of a tooltip based on the anchor element's position
 * and the viewport dimensions. It applies flip logic and nudging to ensure the tooltip
 * remains visible within the viewport, and calculates styles for both the tooltip and its arrow.
 *
 * @param props - The properties for the tooltip positioning. @see {@link IUseSmartTootipPositionProps}.
 *
 * @returns - An object containing the final position of the tooltip, styles for the tooltip, and
 * styles for the arrow. The `finalPosition` indicates the position of the tooltip, while
 * `nudgeStyle` contains the CSS styles to apply to the tooltip, and `triangleOffset` contains the
 * styles for the arrow.
 */
export function useSmartTooltipPosition({
  isActive,
  preferredPosition,
  tooltipRef,
  anchorRef,
  threshold = 15
}: IUseSmartTooltipPositionProps): IUseSmartTooltipPositionResult {
  // After import {CSSProperties} from 'react';
  const [position, setPosition] = useState<TooltipPosition>(preferredPosition);
  const [tooltipStyles, setTooltipStyles] = useState<CSSProperties>({});
  const [arrowStyles, setArrowStyles] = useState<CSSProperties>({});

  /**
   * Updates the tooltip position based on the anchor element's position
   * and the viewport dimensions, applying flip logic and nudging as necessary.
   * It calculates the final position, styles for the tooltip, and arrow alignment.
   */
  function updatePosition(): void {
    const tooltipEl = tooltipRef.current!;
    const anchorEl = anchorRef.current!;
    const viewport = { width: window.innerWidth, height: window.innerHeight };

    const anchorRect = anchorEl.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();

    const finalPosition = getFlippedPosition(
      preferredPosition,
      anchorRect,
      tooltipRect,
      viewport,
      threshold
    );

    let styles = calculateBasePosition(
      finalPosition,
      anchorRect,
      tooltipRect,
      window.scrollX
    );

    styles = applyNudge(styles, tooltipRect, viewport, threshold);
    const arrow = calculateArrowOffset(finalPosition, anchorRect, styles);

    setPosition(finalPosition);
    setTooltipStyles(styles);
    setArrowStyles(arrow);
  }

  // Update position on initial render and when dependencies change.
  useLayoutEffect(() => {
    if (!isActive || !tooltipRef.current || !anchorRef.current) return;
    updatePosition();

    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isActive, preferredPosition, threshold, tooltipRef, anchorRef]);

  return {
    finalPosition: position,
    nudgeStyle: tooltipStyles,
    coordinates: tooltipStyles,
    triangleOffset: arrowStyles.left ?? arrowStyles.top
  };
}
